import { useState, useEffect, useCallback } from 'react';
import {
  Database,
  Play,
  RotateCcw,
  Trash2,
  Clock,
  HardDrive,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Calendar,
  Eye,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import adminApi from '../../services/api/admin';

import { PageTitle } from './shared';
import RestoreBackupModal from './RestoreBackupModal';
import BackupDetailsModal from './BackupDetailsModal';

const BackupsManager = () => {
  const [backups, setBackups] = useState([]);
  const [status, setStatus] = useState({ status: 'idle' });
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);

  // Modals
  const [restoreModal, setRestoreModal] = useState({ open: false, backup: null });
  const [detailsModal, setDetailsModal] = useState({ open: false, backup: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, backupId: null });

  // Polling for status updates
  const [polling, setPolling] = useState(false);

  // Load backups list
  const loadBackups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.backups.list({ limit, offset });
      setBackups(response.backups);
      setTotal(response.total);
    } catch (error) {
      console.error('Failed to load backups:', error);
    } finally {
      setLoading(false);
    }
  }, [limit, offset]);

  // Load backup status
  const loadStatus = useCallback(async () => {
    try {
      const statusData = await adminApi.backups.getStatus();
      setStatus(statusData);

      // Start polling if operation is running
      if (statusData.status === 'running' && !polling) {
        setPolling(true);
      } else if (statusData.status !== 'running' && polling) {
        setPolling(false);
        // Reload backups list when operation completes
        await loadBackups();
      }
    } catch (error) {
      console.error('Failed to load status:', error);
    }
  }, [polling, loadBackups]);

  // Initial load
  useEffect(() => {
    loadBackups();
    loadStatus();
  }, [loadBackups, loadStatus]);

  // Polling effect
  useEffect(() => {
    if (!polling) return;

    const interval = setInterval(() => {
      loadStatus();
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(interval);
  }, [polling, loadStatus]);

  // Trigger manual backup
  const handleTriggerBackup = async () => {
    try {
      await adminApi.backups.trigger();
      setPolling(true);
      setTimeout(loadStatus, 1000);
    } catch (error) {
      console.error('Failed to trigger backup:', error);
      alert('Failed to start backup: ' + error.message);
    }
  };

  // Open restore modal
  const handleOpenRestore = (backup) => {
    setRestoreModal({ open: true, backup });
  };

  // Close restore modal
  const handleCloseRestore = () => {
    setRestoreModal({ open: false, backup: null });
  };

  // Open details modal
  const handleOpenDetails = (backup) => {
    setDetailsModal({ open: true, backup });
  };

  // Close details modal
  const handleCloseDetails = () => {
    setDetailsModal({ open: false, backup: null });
  };

  // Open restore from details modal
  const handleRestoreFromDetails = (backup) => {
    setDetailsModal({ open: false, backup: null });
    setRestoreModal({ open: true, backup });
  };

  // Confirm restore
  const handleConfirmRestore = async (backupId, selectedCollections) => {
    try {
      await adminApi.backups.restore(
        backupId,
        selectedCollections.length > 0 ? selectedCollections : null
      );
      handleCloseRestore();
      setPolling(true);
      setTimeout(loadStatus, 1000);
    } catch (error) {
      console.error('Failed to restore backup:', error);
      alert('Failed to start restore: ' + error.message);
    }
  };

  // Delete backup
  const handleDeleteBackup = async () => {
    const { backupId } = deleteDialog;
    try {
      await adminApi.backups.delete(backupId);
      setDeleteDialog({ open: false, backupId: null });
      await loadBackups();
    } catch (error) {
      console.error('Failed to delete backup:', error);
      alert('Failed to delete backup: ' + error.message);
    }
  };

  // Format bytes to human-readable
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Format duration
  const formatDuration = (start, end) => {
    if (!start || !end) return '-';
    const duration = new Date(end) - new Date(start);
    const seconds = Math.floor(duration / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Status badge component
  const StatusBadge = ({ status: backupStatus }) => {
    const variants = {
      completed: { variant: 'success', icon: CheckCircle2, label: 'Completed' },
      failed: { variant: 'destructive', icon: XCircle, label: 'Failed' },
      running: { variant: 'default', icon: Loader2, label: 'Running' },
    };

    const config = variants[backupStatus] || variants.completed;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`h-3 w-3 ${backupStatus === 'running' ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
    );
  };

  // Trigger badge component
  const TriggerBadge = ({ trigger }) => {
    const colors = {
      scheduled: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300',
      manual: 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300',
      pre_restore: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300',
      restore: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300',
    };

    const labels = {
      scheduled: 'Scheduled',
      manual: 'Manual',
      pre_restore: 'Pre-Restore',
      restore: 'Restore',
    };

    return (
      <Badge variant="outline" className={colors[trigger] || 'bg-stone-100 dark:bg-stone-700 text-stone-800 dark:text-stone-200'}>
        {labels[trigger] || trigger}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <PageTitle
        title="Database Backups"
        description="MongoDB backup and restore management"
        icon={<Database className="h-6 w-6" />}
        actions={
          <Button
            onClick={handleTriggerBackup}
            disabled={status.status === 'running'}
            className="bg-gradient-to-br from-red-800 to-red-900 hover:from-amber-600 hover:to-amber-700"
          >
            <Play className="h-4 w-4 mr-2" />
            Trigger Backup
          </Button>
        }
      />

      {/* Status Banner */}
      {status.status === 'running' && (
        <Card className="border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <Loader2 className="h-6 w-6 text-blue-600 dark:text-blue-400 animate-spin flex-shrink-0 mt-0.5" />
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 dark:text-gray-100">
                    {status.operation === 'backup' ? 'Backup in Progress' : 'Restore in Progress'}
                  </h3>
                  <p className="text-sm text-stone-600 dark:text-stone-300 mt-1">{status.message}</p>
                </div>
                {status.total > 0 && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm text-stone-600 dark:text-stone-300">
                      <span>Progress</span>
                      <span>{status.progress} / {status.total} collections</span>
                    </div>
                    <Progress
                      value={(status.progress / status.total) * 100}
                      className="h-2"
                    />
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="admin-card">
        <CardContent className="pt-6">
          <CardDescription>
            Daily automated backups at 9 AM UTC (4 AM EST). Manual backups available on demand.
            Backups are stored in S3 and automatically deleted after 30 days.
          </CardDescription>
        </CardContent>
      </Card>

      {/* Backups Table */}
      <Card className="admin-card">
        <CardHeader>
          <CardTitle>Backup History</CardTitle>
          <CardDescription>
            {total > 0 ? `${total} backup${total !== 1 ? 's' : ''} total` : 'No backups yet'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-12">
              <Database className="h-12 w-12 text-stone-300 dark:text-stone-600 mx-auto mb-3" />
              <p className="text-stone-500 dark:text-stone-400">No backups available</p>
              <p className="text-sm text-stone-400 dark:text-stone-500 mt-1">
                Trigger a manual backup or wait for the scheduled daily backup
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Collections</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.map((backup) => (
                    <TableRow key={backup.backup_id}>
                      <TableCell className="font-medium text-stone-800 dark:text-stone-100">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-stone-400" />
                          {formatTimestamp(backup.started_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={backup.status} />
                      </TableCell>
                      <TableCell>
                        <TriggerBadge trigger={backup.trigger} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-stone-700 dark:text-stone-300">
                          <HardDrive className="h-4 w-4 text-stone-400" />
                          {backup.collections?.length || 0}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-stone-600 dark:text-stone-300">
                        {backup.total_size_bytes ? formatBytes(backup.total_size_bytes) : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-stone-600 dark:text-stone-300">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-stone-400" />
                          {formatDuration(backup.started_at, backup.completed_at)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenDetails(backup)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4 text-stone-600 dark:text-stone-400" />
                          </Button>
                          {backup.status === 'completed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenRestore(backup)}
                              disabled={status.status === 'running'}
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Restore
                            </Button>
                          )}
                          {backup.status !== 'running' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteDialog({ open: true, backupId: backup.backup_id })}
                              disabled={status.status === 'running'}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {total > limit && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-stone-200 dark:border-stone-700">
                  <p className="text-sm text-stone-600 dark:text-stone-300">
                    Showing {offset + 1} to {Math.min(offset + limit, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={offset === 0}
                      onClick={() => setOffset(Math.max(0, offset - limit))}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={offset + limit >= total}
                      onClick={() => setOffset(offset + limit)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Details Modal */}
      {detailsModal.open && (
        <BackupDetailsModal
          backup={detailsModal.backup}
          onClose={handleCloseDetails}
          onRestore={handleRestoreFromDetails}
        />
      )}

      {/* Restore Modal */}
      {restoreModal.open && (
        <RestoreBackupModal
          backup={restoreModal.backup}
          onClose={handleCloseRestore}
          onConfirm={handleConfirmRestore}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, backupId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Delete Backup
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this backup? This action cannot be undone.
              The backup files will be permanently removed from S3.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBackup} className="bg-gradient-to-r from-red-800 to-red-900 text-amber-50 border border-amber-600/30 hover:from-red-700 hover:to-red-800">
              Delete Backup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BackupsManager;
